/**
 * quotePdf.js
 * Generates a professional Nexus Seals quotation PDF using jsPDF.
 * Always uses the data passed in — never caches or stores a snapshot.
 * Call with fresh Firestore data to guarantee the PDF reflects the latest version.
 */

import jsPDF from 'jspdf'

const BRAND = {
  dark: [27, 45, 62],
  mid:   [100, 116, 139],
  light: [248, 250, 252],
  line:  [226, 232, 240],
  white: [255, 255, 255],
  green: [5, 150, 105],
}

const COMPANY = {
  name:    'Nexus Seals (Pty) Ltd',
  address: '9 David Street, Malvern, Johannesburg, 2094, Gauteng',
  tel:     '011 616 4777',
  web:     'www.nexusseals.com',
  taxNo:   '4390203042',
  regNo:   '2002/016379/07',
  vat:     15,
}

const TERMS_MAP = {
  0: 'Current',
  1: '30 Days',
  2: '60 Days',
  3: '90 Days',
  4: '120 Days',
  5: '150 Days',
  6: '180 Days',
}

const DISCLAIMER =
  'This quote is based on the current availability of stock and is subject to change. ' +
  'We cannot guarantee the availability of the items quoted as our inventory moves quickly. ' +
  'Prices and stock status are valid at the time of this quote and are subject to confirmation upon order placement. ' +
  'Thank you for your understanding.'

function fmt(v) {
  return 'R ' + Number(v || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
}

function setFont(doc, size, style = 'normal', color = BRAND.dark) {
  doc.setFontSize(size)
  doc.setFont('helvetica', style)
  doc.setTextColor(...color)
}

function hLine(doc, y, x1 = 14, x2 = 196, color = BRAND.line, lw = 0.3) {
  doc.setDrawColor(...color)
  doc.setLineWidth(lw)
  doc.line(x1, y, x2, y)
}

function rect(doc, x, y, w, h, fillColor, strokeColor = null) {
  doc.setFillColor(...fillColor)
  if (strokeColor) {
    doc.setDrawColor(...strokeColor)
    doc.setLineWidth(0.3)
    doc.rect(x, y, w, h, 'FD')
  } else {
    doc.rect(x, y, w, h, 'F')
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas  = document.createElement('canvas')
      canvas.width  = img.naturalWidth
      canvas.height = img.naturalHeight
      canvas.getContext('2d').drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = reject
    img.src = src
  })
}

function drawTableHeader(doc, y, COL, PW, ML, MR, CW) {
  rect(doc, ML, y, CW, 8, BRAND.dark)
  setFont(doc, 7, 'bold', BRAND.white)
  doc.text('#',           COL.num.x + 2,             y + 5.5)
  doc.text('Code',        COL.code.x,                y + 5.5)
  doc.text('Description', COL.desc.x,                y + 5.5)
  doc.text('Qty',         COL.qty.x + COL.qty.w,     y + 5.5, { align: 'right' })
  doc.text('Unit Price',  COL.price.x + COL.price.w, y + 5.5, { align: 'right' })
  doc.text('Disc%',       COL.disc.x + COL.disc.w,   y + 5.5, { align: 'right' })
  doc.text('Total Excl.', COL.total.x + COL.total.w, y + 5.5, { align: 'right' })
  return y + 8
}

export async function generateQuotePdf(quote) {
  // quote must be the latest Firestore data — passed in fresh by the caller
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const PW = 210
  const PH = 297
  const ML = 14
  const MR = 14
  const CW = PW - ML - MR

  const termsLabel = TERMS_MAP[quote.accountTerms] ?? '30 Days'

  // Load logo
  let logoDataUrl = null
  try {
    const origin = window.location.origin
    logoDataUrl = await loadImage(`${origin}/logo-full-dark.png`)
  } catch (e) {}

  // ── Header bar ──────────────────────────────────────────────────────────
  rect(doc, 0, 0, PW, 38, BRAND.dark)
  if (logoDataUrl) {
    await new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const ratio = img.naturalWidth / img.naturalHeight || 1
        const h = 34
        const w = h * ratio
        doc.addImage(logoDataUrl, 'PNG', 0, 2, w, h, undefined, 'FAST')
        resolve()
      }
      img.onerror = resolve
      img.src = logoDataUrl
    })
  } else {
    setFont(doc, 18, 'bold', BRAND.white)
    doc.text('Nexus Seals', ML, 18)
    setFont(doc, 7, 'normal', [150, 170, 195])
    doc.text('Working With People To Engineer Solutions', ML, 24)
  }
  setFont(doc, 20, 'bold', BRAND.white)
  doc.text('QUOTATION', PW - MR, 19, { align: 'right' })
  setFont(doc, 7, 'normal', [150, 170, 195])
  doc.text(quote.quoteNumber || '', PW - MR, 26, { align: 'right' })
  // Revision number in header (feature 6)
  if (quote.currentRevision > 1) {
    setFont(doc, 6.5, 'normal', [150, 170, 195])
    doc.text(`Rev ${quote.currentRevision}`, PW - MR, 32, { align: 'right' })
  }

  // ── Company info + meta box ─────────────────────────────────────────────
  let y = 46
  setFont(doc, 8, 'bold', BRAND.dark)
  doc.text(COMPANY.name, ML, y)
  setFont(doc, 7.5, 'normal', BRAND.mid)
  y += 4.5; doc.text(COMPANY.address, ML, y)
  y += 4.5; doc.text(`Tel: ${COMPANY.tel}  |  ${COMPANY.web}`, ML, y)
  y += 4.5; doc.text(`Tax No: ${COMPANY.taxNo}  |  Reg: ${COMPANY.regNo}`, ML, y)

  const boxX = 128
  const boxW = PW - MR - boxX
  let by = 44
  const metaRows = [
    ['Quote No',    quote.quoteNumber || '—'],
    ['Date',        new Date().toISOString().slice(0, 10)],
    ['Valid Until', quote.validUntil || '—'],
    ['Sales Rep',   quote.createdBy   || '—'],
    ['Terms',       termsLabel],
  ]
  if (quote.currentRevision > 1) {
    metaRows.push(['Revision', `Rev ${quote.currentRevision}`])
  }
  metaRows.forEach(([label, value]) => {
    rect(doc, boxX, by, boxW, 7.5, BRAND.light, BRAND.line)
    setFont(doc, 6.5, 'bold', BRAND.mid)
    doc.text(label, boxX + 2, by + 5)
    setFont(doc, 7, 'bold', BRAND.dark)
    doc.text(String(value), boxX + boxW - 2, by + 5, { align: 'right' })
    by += 7.5
  })

  y = Math.max(y + 4, by + 4)
  hLine(doc, y, ML, PW - MR, BRAND.line, 0.4)
  y += 6

  // ── Bill To / Deliver To ────────────────────────────────────────────────
  const colMid = ML + CW / 2 + 4
  setFont(doc, 7, 'bold', BRAND.mid)
  doc.text('BILL TO', ML, y)
  doc.text('DELIVER TO', colMid, y)
  y += 4.5

  const billLines = [
    quote.customerName      || '—',
    quote.accountCode       ? `Account: ${quote.accountCode}` : '',
    quote.contactPerson     || '',
    quote.contactEmail      || '',
    quote.customerReference ? `Ref: ${quote.customerReference}` : '',
  ].filter(Boolean)

  const shipLines = [
    quote.customerName      || '—',
    quote.customerReference ? `Ref: ${quote.customerReference}` : '',
  ].filter(Boolean)

  setFont(doc, 8, 'bold', BRAND.dark)
  doc.text(billLines[0], ML, y)
  doc.text(shipLines[0], colMid, y)
  setFont(doc, 7.5, 'normal', BRAND.mid)
  billLines.slice(1).forEach((l, i) => doc.text(l, ML,     y + (i + 1) * 4.5))
  shipLines.slice(1).forEach((l, i) => doc.text(l, colMid, y + (i + 1) * 4.5))
  y += Math.max(billLines.length, shipLines.length) * 4.5 + 6
  hLine(doc, y, ML, PW - MR, BRAND.line, 0.4)
  y += 6

  // ── Line items table ────────────────────────────────────────────────────
  const COL = {
    num:   { x: ML,       w: 8  },
    code:  { x: ML + 8,   w: 30 },
    desc:  { x: ML + 38,  w: 68 },
    qty:   { x: ML + 106, w: 14 },
    price: { x: ML + 120, w: 26 },
    disc:  { x: ML + 146, w: 14 },
    total: { x: ML + 160, w: 22 },
  }

  y = drawTableHeader(doc, y, COL, PW, ML, MR, CW)

  const items = quote.lineItems || []
  items.forEach((item, i) => {
    // Feature: flexible note — 0-3 lines
    const noteText  = (item.note || '').trim()
    const noteLines = noteText
      ? doc.splitTextToSize(noteText, COL.desc.w - 2).slice(0, 3)
      : []
    const rowH = 9 + (noteLines.length * 4)

    // Page break check — reprint header on new page
    if (y + rowH > PH - 80) {
      doc.addPage()
      y = 20
      y = drawTableHeader(doc, y, COL, PW, ML, MR, CW)
    }

    if (i % 2 === 1) rect(doc, ML, y, CW, rowH, BRAND.light)
    hLine(doc, y + rowH, ML, PW - MR, BRAND.line, 0.2)

    setFont(doc, 7, 'normal', BRAND.mid)
    doc.text(String(i + 1), COL.num.x + 2, y + 6)
    setFont(doc, 7, 'bold', [67, 56, 202])
    doc.text(item.code || '—', COL.code.x, y + 6)
    setFont(doc, 7.5, 'normal', BRAND.dark)
    doc.text(doc.splitTextToSize(item.description || '(no description)', COL.desc.w - 2)[0], COL.desc.x, y + 6)

    noteLines.forEach((noteLine, ni) => {
      setFont(doc, 6.5, 'italic', BRAND.mid)
      doc.text(noteLine, COL.desc.x, y + 10 + (ni * 4))
    })

    const valY = y + 6
    setFont(doc, 7.5, 'normal', BRAND.dark)
    doc.text(String(item.qty),    COL.qty.x + COL.qty.w,     valY, { align: 'right' })
    doc.text(fmt(item.unitPrice), COL.price.x + COL.price.w, valY, { align: 'right' })
    if (item.discount > 0) {
      setFont(doc, 7.5, 'normal', BRAND.green)
      doc.text(`${item.discount}%`, COL.disc.x + COL.disc.w, valY, { align: 'right' })
    } else {
      setFont(doc, 7.5, 'normal', BRAND.mid)
      doc.text('—', COL.disc.x + COL.disc.w, valY, { align: 'right' })
    }
    setFont(doc, 7.5, 'bold', BRAND.dark)
    doc.text(fmt(item.lineTotal), COL.total.x + COL.total.w, valY, { align: 'right' })

    y += rowH
  })

  y += 4

  // ── Totals ──────────────────────────────────────────────────────────────
  const totX = ML + CW - 72
  const totW = 72
  const vatAmount  = (quote.totalExcl || 0) * (COMPANY.vat / 100)
  const grandTotal = (quote.totalExcl || 0) + vatAmount

  const totRows = [
    { label: `Subtotal (${items.length} item${items.length !== 1 ? 's' : ''})`, value: fmt(quote.subtotal), green: false },
    ...(quote.totalDiscount > 0 ? [{ label: 'Discount', value: `– ${fmt(quote.totalDiscount)}`, green: true }] : []),
    { label: 'Total Excl. VAT', value: fmt(quote.totalExcl), green: false },
    { label: `VAT (${COMPANY.vat}%)`, value: fmt(vatAmount), green: false },
  ]
  totRows.forEach(row => {
    rect(doc, totX, y, totW, 7, BRAND.light, BRAND.line)
    setFont(doc, 6.5, 'normal', row.green ? BRAND.green : BRAND.mid)
    doc.text(row.label, totX + 3, y + 5)
    setFont(doc, 7, 'normal', row.green ? BRAND.green : BRAND.dark)
    doc.text(row.value, totX + totW - 3, y + 5, { align: 'right' })
    y += 7
  })
  rect(doc, totX, y, totW, 9, BRAND.dark)
  setFont(doc, 7, 'bold', BRAND.white)
  doc.text('TOTAL INCL. VAT', totX + 3, y + 6)
  setFont(doc, 8, 'bold', BRAND.white)
  doc.text(fmt(grandTotal), totX + totW - 3, y + 6, { align: 'right' })
  y += 13

  // ── Notes ───────────────────────────────────────────────────────────────
  if (quote.customerNotes) {
    setFont(doc, 7, 'bold', BRAND.mid)
    doc.text('NOTES', ML, y)
    y += 4.5
    setFont(doc, 7.5, 'normal', BRAND.dark)
    const noteLines = doc.splitTextToSize(quote.customerNotes, CW)
    doc.text(noteLines, ML, y)
    y += noteLines.length * 4.5 + 4
  }

  // ── Disclaimer ──────────────────────────────────────────────────────────
  y += 2
  hLine(doc, y, ML, PW - MR, BRAND.line, 0.3)
  y += 5
  setFont(doc, 7, 'bold', BRAND.mid)
  doc.text('DISCLAIMER', ML, y)
  y += 4.5
  setFont(doc, 6.5, 'normal', BRAND.mid)
  const disclaimerLines = doc.splitTextToSize(DISCLAIMER, CW)
  doc.text(disclaimerLines, ML, y)
  y += disclaimerLines.length * 4 + 4

  // ── Footer ──────────────────────────────────────────────────────────────
  const footY = PH - 18
  hLine(doc, footY, ML, PW - MR, BRAND.line, 0.4)

  if (quote.portalUrl) {
    setFont(doc, 6.5, 'normal', [67, 56, 202])
    doc.text(`View online: ${quote.portalUrl}`, ML, footY + 7)
  }

  const revSuffix = quote.currentRevision > 1 ? `  ·  Rev ${quote.currentRevision}` : ''
  setFont(doc, 6.5, 'normal', BRAND.mid)
  doc.text(`${quote.quoteNumber}${revSuffix}  ·  Page 1 of 1`, PW / 2, PH - 4, { align: 'center' })

  return doc.output('blob')
}

export function downloadPdfBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a   = document.createElement('a')
  a.href     = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}